import { Dispatch, RefObject, SetStateAction } from "react";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import {
  getCashRequests,
  GetCashRequestsProps,
} from "@/services/cash-requests.service";
import { CRFRProps, getCashRequestFilter } from "./filter-obj";
import { getCuentasByBusqueda } from "@/services/accounts.service";
import { Option } from "@/components/select/AsyncAutocompleteSelect";
import { AppRoutes } from "@/constants/routes";
import { handlePermissionError } from "@/utils/permission-check";
import { toaster } from "@/components/ui/toaster";
import { AxiosError } from "axios";
import { NavigateFunction } from "react-router-dom";

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
}

export const clearFiltersHandler = ({
  setFiltersApplied,
  setValue,
  getValues,
  submitFilters,
  setOpenFilters,
}: ClearFiltersHandlerProps) => {
  setValue("request_date", undefined);
  setValue("account_owner", "");
  setValue("sucursal", []);
  setValue("request_amount", "");
  setValue("requester", "");
  setValue("account_number", "");
  setValue("balance_control", []);
  setValue("estados", []);

  submitFilters?.(getValues());
  setOpenFilters(false)
  setFiltersApplied(false);
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<SetStateAction<{}>>;
  fetchCashRequests: (filters?: any) => Promise<void>;
  event: FieldValues;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  scheduled?: boolean;
}

export const submitFiltersHandler = async ({
  event,
  setCurrentFilters,
  setFiltersApplied,
  fetchCashRequests,
  setOpenFilters,
  navigate,
  fetchPermissions,
  scheduled = false,
}: SubmitFiltersHandlerProps) => {
  try {
    setFiltersApplied(true);

    const filterObj: Partial<GetCashRequestsProps> = getCashRequestFilter(
      event as CRFRProps,
      scheduled,
    );

    setCurrentFilters(filterObj);

    if (!Object.keys(filterObj)?.length) setFiltersApplied(false);

    await fetchCashRequests(filterObj);

    setOpenFilters(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.CASH_REQUESTS,
    );
    if (!handled) {
      toaster.create({
        title:
          errorObj?.response?.data?.message ??
          "No se pudieron aplicar los filtros",
        type: "error",
      });
    }
    throw error;
  }
};

export interface LoadOptionsHandlerProps {
  timeoutRef: RefObject<NodeJS.Timeout | null>;
  inputValue: string;
  setLoadingPersons: Dispatch<SetStateAction<boolean>>;
  getOptions: (inputValue: string) => Promise<
    {
      label: string;
      value: string;
      tipoCuenta: string;
      idTipoCuenta: number;
    }[]
  >;
}

export interface GetAccountOptionsHandlerProps {
  inputValue: string;
}

export const getOptionsHandler = async ({
  inputValue,
}: GetAccountOptionsHandlerProps) => {
  const filtered = await getCuentasByBusqueda({
    busqueda: inputValue,
  });

  return (
    filtered?.map((p) => ({
      label: `${p.titular} - ${p.nroCuenta} - ${p.tipoCuenta}`,
      value: p.nroCuenta,
      tipoCuenta: p.tipoCuenta,
      idTipoCuenta: p.idTipoCuenta,
    })) ?? []
  );
};

export const loadOptionsHandler = ({
  timeoutRef,
  inputValue,
  getOptions,
  setLoadingPersons,
}: LoadOptionsHandlerProps): Promise<Option[]> => {
  return new Promise((resolve) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef?.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (inputValue.trim().length < 3) {
        resolve([]);
        return;
      }

      setLoadingPersons(true);
      const options = getOptions(inputValue);

      setLoadingPersons(false);
      resolve(options);
    }, 1000);
  });
};

interface GetCashRequestsHandlerProps {
  showLoad?: boolean;
  setLoadingCashRequests: Dispatch<SetStateAction<boolean>>;
  setCashRequests: Dispatch<SetStateAction<any>>;
  filters?: any;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  programadas?: boolean;
}

export const fetchCashRequestsHandler = async ({
  showLoad,
  setLoadingCashRequests,
  setCashRequests,
  filters,
  navigate,
  fetchPermissions,
  programadas = false,
}: GetCashRequestsHandlerProps) => {
  try {
    if (showLoad) setLoadingCashRequests(true);

    //await this func when data is available
    const response = await getCashRequests({
      ...filters,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": 10,
      Programadas: programadas,
    });

    setCashRequests(response);

    setLoadingCashRequests(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.message ??
          (programadas
            ? "No se pudo cargar la lista de solicitudes de efectivo programadas."
            : "No se pudo cargar la lista de solicitudes."),
      });
    }
    throw error;
  }
};
